import {
  Component, effect,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  OnInit,
  Output,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';

import { flagsPosition } from '@shared/utils';

import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  FormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { fromEvent, Subscription } from 'rxjs';
import { NgClass } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { LanguageStore } from '@settings/language/data-access';
import { TelInputStore } from './data-access/tel-input.store';
import { ICountryCodesPhoneNumber } from './data-access/entities/tel-input.model';
import { SkeletonLoaderComponent } from '@shared/components/ui/skeleton-loader';
import { ScrollAreaComponent } from '@shared/components/ui/scroll-area';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { ToggleElementDirective } from '@shared/directives';

type TState = 'default' | 'disabled' | 'error';

/**
 * Telephone input component with country code selection
 * @example
 * <lib-tel-input
 *   [name]="'telephone'"
 *   [id]="'telephone'"
 *   [regionFixed]="'BR'"
 *   [(state)]="fieldState"
 *   (activeCountryObjectChange)="onCountryChange($event)"
 *   [(ngModel)]="telephone"
 * ></lib-tel-input>
 */
@Component({
    selector: 'lib-tel-input',
    templateUrl: './tel-input.component.html',
    styleUrls: ['./tel-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: TelInputComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: TelInputComponent,
            multi: true,
        },
    ],
    imports: [
      NgxMaskDirective,
      FormsModule,
      NgClass,
      SkeletonLoaderComponent,
      ScrollAreaComponent,
      ToggleElementDirective
    ]
})
export class TelInputComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {

  /** Name attribute for the input */
  @Input() name = '';
  /** ID attribute for the input */
  @Input() id = '';
  /** Emits when the selected country changes */
  @Output() activeCountryObjectChange: EventEmitter<ICountryCodesPhoneNumber> =
    new EventEmitter<ICountryCodesPhoneNumber>();
  
  /**
   * The state of the input
   * @default 'default'
   * @type 'default' | 'disabled' | 'error'
   */
  public readonly state: ModelSignal<TState> = model<TState>('default');

  /** Internal value of the input */
  public _value: string;
  /** Telephone input store instance */
  protected readonly telInputStore = inject(TelInputStore);
  /** Language store instance */
  private readonly languageStore = inject(LanguageStore);
  /** Loading state of the component */
  public readonly loading: InputSignal<boolean> = input<boolean>();

  public readonly fullNumber: InputSignal<string> = input<string>();

  public readonly regionFixed: InputSignal<string> = input<string>();

  /**
   * Validates the number against the selected country's mask (`mask` error).
   * Opt-in: masks are mobile-shaped, so a landline that fits no mask (10 digits
   * against Brazil's `(00) 00000-0000`) would turn existing forms invalid.
   * @default false
   */
  public readonly validateMask: InputSignal<boolean> = input<boolean>(false);

  /** Controls visibility of country dropdown list */
  public readonly showCountryList: WritableSignal<boolean> = signal(false);
  /** Keyup listener, subscribed only while the country list is open */
  private keyupSubscription: Subscription | null = null;
  /** Available flag positions */
  public flagsPosition = flagsPosition;
  /** Phone number country code translations */
  public telephoneTranslation = this.telInputStore.phoneNumberCountryCode;
  /** Search expression for country filtering */
  private searchExpr = '';
  /** Timer for clearing search expression */
  private timerToClearSearch: any = null;
  /** Tracks if the input has been touched */
  private touched = false;

  /** Currently selected country object */
  public activeCountryObject: ICountryCodesPhoneNumber = {
    Region: 'BR',
    CountryDisplayName: 'Brasil',
    CountryCode: 55,
    MobileMask: '(00) 00000-0000',
    FullNumber: null
  };
  
  public readonly onFullNumber = effect(() => {
    if (this.fullNumber() && this.telephoneTranslation()) {
      untracked(() => {
        const phoneNumber = parsePhoneNumberFromString(this.fullNumber());
        const country = phoneNumber?.country;
  
        if (country) {
          const countryObj = this.telephoneTranslation().find(
            (item: ICountryCodesPhoneNumber) => item.Region === country
          );
          
          if (countryObj) {
            const newCountryObj = { 
              ...countryObj, 
              FullNumber: `+${countryObj.CountryCode}${this._value}` 
            };
            
            this.activeCountryObject = newCountryObj;
            this.activeCountryObjectChange.emit(newCountryObj);
            // Fora do ciclo em curso: o pai pode já ter lido a validade do controlo
            queueMicrotask(() => this.onValidatorChange?.());
          }
        }
      });
    }
  });

  /**
   * Stops letter keys pressed on the open list from reaching global shortcuts
   * (e.g. "B" focuses the navbar search), so the country search can use them
   * @param event Keydown event from the country list
   */
  public keepSearchKeyLocal(event: KeyboardEvent): void {
    if (event.key.length === 1) {
      event.stopPropagation();
    }
  }

  /** Selects the fixed region as the active country once the list is available */
  public readonly onRegionFixed = effect(() => {
    const region = this.regionFixed();
    const countries = this.telephoneTranslation();

    if (!region || !countries) return;

    untracked(() => {
      const countryObj = countries.find((item: ICountryCodesPhoneNumber) => item.Region === region);

      if (countryObj) {
        this.activeCountryObject = { ...countryObj };
        this.activeCountryObjectChange.emit(this.activeCountryObject);
        queueMicrotask(() => this.onValidatorChange?.());
      } else {
        // Dropdown fica oculto: sem este aviso o país padrão seria usado em silêncio
        console.warn(`lib-tel-input: regionFixed "${region}" não existe na lista de países; usando o padrão.`);
      }
    });
  });

  /** Component constructor */
  constructor() {
    // Carga inicial e recarga por mudança de idioma vivem no mesmo `effect`:
    // `getCurrentLanguage` é um `computed`, logo o efeito corre uma vez na
    // inicialização e de novo a cada troca de idioma. Uma chamada extra aqui no
    // construtor seria imediatamente cancelada pelo `switchMap` do `rxMethod`.
    effect(() => {
      this.languageStore.getCurrentLanguage();
      this.telInputStore.loadCountryCodesPhoneNumber();
    });
  }

  /**
   * Handles changes in search expression for country filtering
   * @private
   */
  searchExprChange(): void {
    if (this.timerToClearSearch) {
      clearTimeout(this.timerToClearSearch);
    }

    this.timerToClearSearch = setTimeout(() => {
      this.searchExpr = '';
    }, 1000);

    const translation: ICountryCodesPhoneNumber = this.telephoneTranslation()?.find((item: ICountryCodesPhoneNumber) =>
      item?.CountryDisplayName?.toLowerCase()?.includes(
        this.searchExpr?.toLowerCase()
      )
    );

    if (translation && this.showCountryList()) {
      const element = document.getElementById(
        translation?.CountryDisplayName
      );
      element.scrollIntoView({ behavior: 'smooth' });
      element.focus();
    }
  }

  /** Keeps the keyup search in sync with the dropdown: listens only while it is open */
  public readonly onShowCountryList = effect(() => {
    const open = this.showCountryList();

    untracked(() => (open ? this.startSearchKeys() : this.stopSearchKeys()));
  });

  /** Starts listening to keyup on the document for country search */
  private startSearchKeys(): void {
    if (this.keyupSubscription || this.regionFixed()) return;

    this.keyupSubscription = fromEvent<KeyboardEvent>(document, 'keyup').subscribe((e) => {
      this.searchExpr += e.key;
      this.searchExprChange();
    });
  }

  /** Stops the keyup listener and resets the pending search */
  private stopSearchKeys(): void {
    this.keyupSubscription?.unsubscribe();
    this.keyupSubscription = null;
    clearTimeout(this.timerToClearSearch);
    this.searchExpr = '';
  }

  /** Initializes the component */
  ngOnInit() {
    this.activeCountryObjectChange.emit(this.activeCountryObject);
  }

  /** Releases the keyup listener and pending search timer */
  ngOnDestroy() {
    this.stopSearchKeys();
  }

  /**
   * Marks the input as touched when it loses focus
   * @returns void
   */
  public barred(): void {
    this.onTouched?.();
    this.updateFormValidity();
  }

  /**
   * Updates the form control validity by propagating the current value
   * @private
   */
  private updateFormValidity(): void {
    this.onChange?.(this._value);
  }

  /**
   * Selects a country from the dropdown
   * @param e Event object
   * @param countryObj Selected country object
   */
  public selectCountry(e: Event, countryObj: ICountryCodesPhoneNumber): void {
    e.preventDefault();
    e.stopPropagation();
    this.activeCountryObject = countryObj;
    this.activeCountryObjectChange.emit(countryObj);
    this.showCountryList.set(false);
    this.onValidatorChange?.();
  }

  /** Re-runs the control's validators — set by `registerOnValidatorChange` */
  private onValidatorChange?: () => void;

  /**
   * Validates the number against the selected country's mask (Validator).
   * In the mask, `0` is a required digit and `9` an optional one. Empty is
   * valid — pair with `Validators.required` when the field is mandatory.
   * @returns `{ mask: { requiredMask } }` when the digit count does not fit the mask
   */
  public validate(control: AbstractControl): ValidationErrors | null {
    if (!this.validateMask()) return null;
    const digits = String(control.value ?? '').replace(/\D/g, '');
    const mask = this.activeCountryObject?.MobileMask;
    if (!digits || !mask) return null;

    const required = (mask.match(/0/g) ?? []).length;
    const optional = (mask.match(/9/g) ?? []).length;
    const fits = digits.length >= required && digits.length <= required + optional;
    return fits ? null : { mask: { requiredMask: mask } };
  }

  /**
   * Registers the callback that re-validates the control (Validator)
   * @param fn Callback function
   */
  public registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  /** Callback for when the input is touched */
  public onTouched: any = () => {};
  /** Callback for when the input value changes */
  public onChange: any = () => {};

  /** Registers the current value and marks as touched */
  public registerValue(): void {
    this.markAsTouched();
    this.onChange(this._value);
  }

  /**
   * Writes a value to the input (ControlValueAccessor)
   * @param value Value to write
   */
  public writeValue(value: string): void {
    this._value = value;
    this.registerValue();
  }

  /**
   * Registers change callback (ControlValueAccessor)
   * @param fn Callback function
   */
  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * Registers touch callback (ControlValueAccessor)
   * @param fn Callback function
   */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /** Marks the input as touched if not already touched */
  public markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  public openRegions(): void {
    if (this.regionFixed()) return;
    this.showCountryList.set(true);
  }
}